import React from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';

interface StatCardProps {
  title: string;
  value: string | number;
  change?: {
    value: number;
    type: 'increase' | 'decrease';
  };
  icon?: React.ReactNode;
  gradient?: 'purple' | 'cyan' | 'green' | 'orange';
  className?: string;
}

export const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  change,
  icon,
  gradient = 'purple',
  className,
}) => {
  const gradients = {
    purple: 'from-primary-600/20 to-primary-900/20',
    cyan: 'from-accent-500/20 to-accent-700/20',
    green: 'from-success-500/20 to-success-700/20',
    orange: 'from-warning-500/20 to-warning-700/20',
  };

  const iconBg = {
    purple: 'bg-primary-500/20 text-primary-400',
    cyan: 'bg-accent-500/20 text-accent-400',
    green: 'bg-success-500/20 text-success-400',
    orange: 'bg-warning-500/20 text-warning-400',
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      whileHover={{
        scale: 1.02,
        transition: { duration: 0.2 }
      }}
      className={clsx(
        'relative overflow-hidden rounded-2xl p-6',
        'bg-gradient-to-br',
        gradients[gradient],
        'border border-[var(--border-color)]',
        'hover:border-[var(--border-color-hover)]',
        'transition-all duration-300',
        className
      )}
    >
      {/* Background glow */}
      <div className="absolute -top-24 -right-24 w-48 h-48 bg-gradient-to-br from-primary-500/10 to-transparent rounded-full blur-3xl" />

      <div className="relative flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-[var(--text-secondary)]">{title}</p>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mt-2 text-3xl font-bold text-[var(--text-primary)]"
          >
            {value}
          </motion.p>

          {change && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className={clsx(
                'mt-2 flex items-center gap-1 text-sm font-medium',
                change.type === 'increase' ? 'text-success-400' : 'text-error-400'
              )}
            >
              <svg
                className={clsx('w-4 h-4', change.type === 'decrease' && 'rotate-180')}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
              </svg>
              <span>{Math.abs(change.value)}%</span>
              <span className="text-[var(--text-muted)]">vs last period</span>
            </motion.div>
          )}
        </div>

        {icon && (
          <div className={clsx('p-3 rounded-xl', iconBg[gradient])}>
            {icon}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default StatCard;
